import type { DomainDataProfile } from '../../core/data-profiles';
import type { Node, Edge } from '../../core/models/scene';
import {
  orgChartHydrator,
  type OrgEdgeType,
  type OrgNodeType,
  type OrgUniversalEdgeProps,
  type OrgUniversalNodeProps,
} from './interop/OrgChartHydrator';
import { ScenarioProjection } from './services/ScenarioProjection';
import { toProfileFieldAliases } from './fields/import-aliases';
import { ORG_CHART_FIELD_PACKS } from './fields/orgChartFieldPacks';
import {
  getOrgChartPersonFieldPacks,
  getOrgChartPositionFieldPacks,
} from './fields/fieldPackToManifest';

function importableFieldEntityRefsFromPacks(): Readonly<Record<string, readonly string[]>> {
  const entityByPack = new Map<string, string>([
    ...getOrgChartPersonFieldPacks().map((packId): [string, string] => [packId, 'person']),
    ...getOrgChartPositionFieldPacks().map((packId): [string, string] => [packId, 'position']),
  ]);
  const refs = new Map<string, Set<string>>();
  for (const pack of ORG_CHART_FIELD_PACKS) {
    const entityRef = entityByPack.get(pack.id);
    if (!entityRef) continue;
    for (const field of pack.fields) {
      const set = refs.get(field.id) ?? new Set<string>();
      set.add(entityRef);
      refs.set(field.id, set);
    }
  }
  return Object.fromEntries([...refs].map(([fieldId, set]) => [fieldId, [...set]]));
}

export const OrgChartProfile: DomainDataProfile<OrgNodeType, OrgEdgeType, OrgUniversalNodeProps, OrgUniversalEdgeProps> = {
  domainId: 'orgchart',
  profileVersion: '1.0',
  diagramTypes: ['org'],
  nodeTypes: ['org_person', 'org_position', 'org_department'],
  edgeTypes: ['reports_to', 'occupies', 'dotted_line_reports', 'matrix_reports', 'team_member', 'assistant_to'],
  vocabulary: {
    recordSingular: 'Employee',
    recordPlural: 'Employees',
    repositoryLabel: 'HR Directory',
    hierarchyLabel: 'Reports to',
    importAction: 'Import Org Chart',
    keyColumnHint: 'Employee ID',
    commitLabel: 'Push to HR System',
    pullLabel: 'Refresh from HR',
    diagramLabel: 'org chart',
  },
  fieldAliases: {
    // Comprehensive aliases generated from field packs (single source of truth)
    ...toProfileFieldAliases(),
    // Additional structural aliases not covered by person field packs
    validFrom: ['validfrom', 'valid_from', 'from'],
    validTo: ['validto', 'valid_to', 'to'],
    level: ['level', 'grade'],
    departmentCode: ['departmentcode', 'department_code'],
  },
  importableFieldIds: ORG_CHART_FIELD_PACKS.flatMap((pack) =>
    pack.fields.map((field) => field.id),
  ),
  importableFieldEntityRefs: importableFieldEntityRefsFromPacks(),
  requiredProps: ['personId', 'name'],
  recommendedProps: ['title', 'department', 'reportsTo'],
  hierarchyConfig: {
    type: 'single-parent',
    parentProp: 'reportsTo',
    edgeSubtype: 'reports_to',
  },
  primaryShapeType: 'org_person',
  propsToAttrsMapping: {
    // Base identity
    personId: 'personId',
    name: 'name',
    firstName: 'firstName',
    lastName: 'lastName',
    preferredName: 'preferredName',
    title: 'role',
    email: 'email',
    phone: 'phone',
    department: 'department',
    reportsTo: 'managerId',
    joinDate: 'joinDate',
    departureDate: 'departureDate',
    photoRef: 'photoRef',
    employeeNumber: 'employeeNumber',
    status: 'status',
    notes: 'notes',
    // Organization
    division: 'division',
    businessUnit: 'businessUnit',
    subDepartment: 'subDepartment',
    costCenter: 'costCenter',
    location: 'location',
    building: 'building',
    floor: 'floor',
    region: 'region',
    country: 'country',
    timezone: 'timezone',
    workMode: 'workMode',
    legalEntity: 'legalEntity',
    // Role
    positionId: 'positionId',
    positionCode: 'positionCode',
    jobFamily: 'jobFamily',
    jobLevel: 'jobLevel',
    managementLevel: 'managementLevel',
    employmentType: 'employmentType',
    fte: 'fte',
    workerType: 'workerType',
    regularTemporary: 'regularTemporary',
    flsaStatus: 'flsaStatus',
    level: 'level',
    unstaffed: 'unstaffed',
    // Talent
    performance: 'performance',
    potential: 'potential',
    nineBoxPlacement: 'nineBoxPlacement',
    readiness: 'readiness',
    flightRisk: 'flightRisk',
    impactOfLoss: 'impactOfLoss',
    futureLeader: 'futureLeader',
    talentPool: 'talentPool',
    calibrationRating: 'calibrationRating',
    lastReviewDate: 'lastReviewDate',
    nextReviewDate: 'nextReviewDate',
    overallRating: 'overallRating',
    talentEngagementScore: 'talentEngagementScore',
    // Compensation
    salary: 'salary',
    currency: 'currency',
    payFrequency: 'payFrequency',
    band: 'band',
    payRangeMin: 'payRangeMin',
    payRangeMid: 'payRangeMid',
    payRangeMax: 'payRangeMax',
    compaRatio: 'compaRatio',
    bonusTarget: 'bonusTarget',
    totalComp: 'totalComp',
    annualCost: 'annualCost',
    lastCompChangeDate: 'lastCompChangeDate',
    // Skills
    skills: 'skills',
    primarySkill: 'primarySkill',
    certifications: 'certifications',
    languages: 'languages',
    educationLevel: 'educationLevel',
    educationField: 'educationField',
    competencies: 'competencies',
    technicalSkills: 'technicalSkills',
    leadershipSkills: 'leadershipSkills',
    trainingCompleted: 'trainingCompleted',
    certExpiryDate: 'certExpiryDate',
    // Workforce
    tenure: 'tenure',
    retirementDate: 'retirementDate',
    contractEndDate: 'contractEndDate',
    probationEndDate: 'probationEndDate',
    lastPromotionDate: 'lastPromotionDate',
    promotionCount: 'promotionCount',
    timeInRole: 'timeInRole',
    previousRole: 'previousRole',
    previousDepartment: 'previousDepartment',
    mobilityPreference: 'mobilityPreference',
    careerInterest: 'careerInterest',
    targetRole: 'targetRole',
    noticePeriod: 'noticePeriod',
    visaStatus: 'visaStatus',
    visaExpiry: 'visaExpiry',
    // Succession
    keyPosition: 'keyPosition',
    successorCount: 'successorCount',
    readyNowSuccessor: 'readyNowSuccessor',
    successionRisk: 'successionRisk',
    benchStrength: 'benchStrength',
    developmentPlan: 'developmentPlan',
    mentor: 'mentor',
    coach: 'coach',
    careerPath: 'careerPath',
    yearsToReadiness: 'yearsToReadiness',
    criticalExperience: 'criticalExperience',
    retentionStrategy: 'retentionStrategy',
    // Demographics
    gender: 'gender',
    pronouns: 'pronouns',
    ethnicity: 'ethnicity',
    nationality: 'nationality',
    dateOfBirth: 'dateOfBirth',
    ageGroup: 'ageGroup',
    generation: 'generation',
    veteranStatus: 'veteranStatus',
    disabilityStatus: 'disabilityStatus',
    maritalStatus: 'maritalStatus',
    diversityFlags: 'diversityFlags',
    // Engagement
    engagementScore: 'engagementScore',
    eNPS: 'eNPS',
    lastSurveyDate: 'lastSurveyDate',
    satisfactionScore: 'satisfactionScore',
    managerRating: 'managerRating',
    absenteeismRate: 'absenteeismRate',
    voluntaryTurnoverRisk: 'voluntaryTurnoverRisk',
    lastOneOnOneDate: 'lastOneOnOneDate',
    recognitionsReceived: 'recognitionsReceived',
    recognitionsGiven: 'recognitionsGiven',
  },
  relationshipPackId: 'orgchart-relationships',
  hydrator: orgChartHydrator,
  shapeBinding: {
    onNewEntityShape: 'prompt',
    onReadOnlySource: 'prompt',
  },
  relationshipMappings: [
    {
      pattern: 'entity-field',
      edgeType: 'reporting',
      fieldOwner: 'from',
      fieldName: 'reportsTo',
      fieldValue: 'to',
      cardinality: '1:1',
    },
  ],
  scenarioConfig: {
    supportedOperations: ['add', 'remove', 'move', 'update'],
    trackableProps: ['name', 'title', 'department', 'email', 'reportsTo'],
    projector: (baseNodes, baseEdges, delta) =>
      ScenarioProjection.applyDelta(
        baseNodes as Node[],
        baseEdges as Edge[],
        delta as Parameters<typeof ScenarioProjection.applyDelta>[2]
      ),
  },
  chromeConfig: {
    // Pen, sticky notes, and text are presentation annotations, not graph edits.
    // Org-card styling is governed by the shared presentation-ownership gate.
    hiddenTools: ['frames'],
    searchTarget: 'people',
  },
  onboardingModes: ['blank', 'demo'],
};
